/* =============================================================================
   forms.jsx  —  RSVP, Well wishes, Baby predictions (validation + save states).
   Now passes a Turnstile token on each public write; the Worker verifies it.
============================================================================= */
import { useState } from 'react';
import { Field, Input, TextArea, Note } from './ui';
import { Butterfly, LeafSprig, Ladybug, Wildflower, Bee } from './decor';
import { Turnstile } from './Turnstile';
import { DB } from '../lib/api';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || '').trim());

/* --------------------------------- RSVP ---------------------------------- */
export function RSVPForm() {
  const blank = {
    firstName: '', lastName: '', email: '', attending: '',
    street: '', apt: '', city: '', state: '', zip: '',
    partySize: '1', partyNames: '', gotGift: false, note: '', privacyAck: false,
  };
  const [f, setF] = useState(blank);
  const [errs, setErrs] = useState({});
  const [status, setStatus] = useState('idle'); // idle | saving | done | error
  const [tsToken, setTsToken] = useState('');
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const accepting = f.attending === 'accept';

  function validate() {
    const e = {};
    if (!f.firstName.trim()) e.firstName = 'Please add your first name.';
    if (!f.lastName.trim()) e.lastName = 'Please add your last name.';
    if (!f.email.trim()) e.email = 'Please add your email.';
    else if (!emailOk(f.email)) e.email = 'That email does not look quite right.';
    if (!f.attending) e.attending = 'Let us know if you can make it.';
    if (accepting) {
      if (!f.street.trim()) e.street = 'Street address is needed to send your invite.';
      if (!f.city.trim()) e.city = 'City is needed.';
      if (!f.state) e.state = 'State is needed.';
      if (!f.zip.trim()) e.zip = 'ZIP is needed.';
      const n = parseInt(f.partySize, 10);
      if (!n || n < 1) e.partySize = 'At least one guest, including you.';
    }
    if (!f.privacyAck) e.privacyAck = 'Please check this so we can save your spot.';
    setErrs(e);
    return Object.keys(e).length === 0;
  }

  async function submit(ev) {
    ev.preventDefault();
    if (status === 'saving' || status === 'done') return;
    if (!validate()) return;
    setStatus('saving');
    try {
      const rec = {
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        email: f.email.trim(),
        attending: f.attending,
        street: accepting ? f.street.trim() : '',
        apt: accepting ? f.apt.trim() : '',
        city: accepting ? f.city.trim() : '',
        state: accepting ? f.state : '',
        zip: accepting ? f.zip.trim() : '',
        partySize: accepting ? parseInt(f.partySize, 10) || 1 : 0,
        partyNames: accepting ? f.partyNames.trim() : '',
        gotGift: accepting ? !!f.gotGift : false,
        note: f.note.trim(),
        privacyAck: f.privacyAck === true, // strict boolean
        turnstileToken: tsToken,
      };
      await DB.addRSVP(rec);
      setStatus('done');
      window.dispatchEvent(new Event('llb:data-changed'));
    } catch (err) {
      console.error(err);
      setStatus('error');
      // Turnstile tokens are single-use; refresh for a clean retry.
      setTsToken('');
      if (window.turnstile) window.turnstile.reset();
    }
  }

  if (status === 'done') {
    return (
      <div className="card center" style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -14, right: -10 }}><Butterfly size={92} className="floaty" /></div>
        <div style={{ position: 'absolute', bottom: -16, left: -6 }}><LeafSprig size={96} /></div>
        <div style={{ position: 'relative' }}>
          <Ladybug size={44} />
          <h3 style={{ fontSize: '2rem', margin: '8px 0 6px' }}>
            {f.attending === 'accept' ? 'You are on the list.' : 'Thank you for letting us know.'}
          </h3>
          <p className="muted" style={{ maxWidth: '38ch', margin: '0 auto' }}>
            {f.attending === 'accept'
              ? 'We cannot wait to celebrate with you. Watch your inbox for the formal invitation.'
              : 'We will miss you, and we are grateful you took a moment to RSVP. Sending love your way.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <form className="card rsvp-form" onSubmit={submit} noValidate>
      <div className="grid-2">
        <Field label="First name" required error={errs.firstName}>
          <Input value={f.firstName} onChange={set('firstName')} invalid={errs.firstName} autoComplete="given-name" />
        </Field>
        <Field label="Last name" required error={errs.lastName}>
          <Input value={f.lastName} onChange={set('lastName')} invalid={errs.lastName} autoComplete="family-name" />
        </Field>
      </div>
      <Field label="Email" required error={errs.email}>
        <Input type="email" value={f.email} onChange={set('email')} invalid={errs.email} autoComplete="email" placeholder="you@email.com" />
      </Field>

      <Field label="Will you join us?" required error={errs.attending}>
        <div className="choice-row">
          <div className={`choice ${f.attending === 'accept' ? 'on' : ''}`} onClick={() => set('attending')('accept')} role="radio" aria-checked={f.attending==='accept'} tabIndex={0}
               onKeyDown={(e)=>{ if(e.key==='Enter'||e.key===' '){e.preventDefault();set('attending')('accept');} }}>
            <span className="dot" /> Joyfully accept
          </div>
          <div className={`choice ${f.attending === 'decline' ? 'on' : ''}`} onClick={() => set('attending')('decline')} role="radio" aria-checked={f.attending==='decline'} tabIndex={0}
               onKeyDown={(e)=>{ if(e.key==='Enter'||e.key===' '){e.preventDefault();set('attending')('decline');} }}>
            <span className="dot" /> Regretfully decline
          </div>
        </div>
      </Field>

      {accepting && (
        <div className="reveal">
          <hr className="hr" />
          <h3 className="rsvp-subhead">Where should we send your invite?</h3>
          <p className="faint" style={{ fontSize: '0.84rem', margin: '0 0 16px' }}>
            We use your address only to mail your invitation and a thank-you card.
          </p>
          <Field label="Street address" required error={errs.street}>
            <Input value={f.street} onChange={set('street')} invalid={errs.street} autoComplete="address-line1" />
          </Field>
          <Field label="Apartment or unit" optional>
            <Input value={f.apt} onChange={set('apt')} autoComplete="address-line2" />
          </Field>
          <div className="grid-3">
            <Field label="City" required error={errs.city}>
              <Input value={f.city} onChange={set('city')} invalid={errs.city} autoComplete="address-level2" />
            </Field>
            <Field label="State" required error={errs.state}>
              <select className={errs.state ? 'invalid' : ''} value={f.state} onChange={(e) => set('state')(e.target.value)}>
                <option value="">Select</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="ZIP" required error={errs.zip}>
              <Input value={f.zip} onChange={set('zip')} invalid={errs.zip} inputMode="numeric" autoComplete="postal-code" />
            </Field>
          </div>
          <div className="grid-2">
            <Field label="Guests in your party" required error={errs.partySize} hint="Including yourself">
              <Input type="number" min="1" value={f.partySize} onChange={set('partySize')} invalid={errs.partySize} />
            </Field>
            <Field label="Names of others in your party" optional hint="Plus-ones and little ones">
              <Input value={f.partyNames} onChange={set('partyNames')} placeholder="e.g. Sam, and baby June" />
            </Field>
          </div>
          <label className="check" style={{ marginBottom: 6 }}>
            <input type="checkbox" checked={f.gotGift} onChange={(e) => set('gotGift')(e.target.checked)} />
            <span>I have already chosen a gift from the registry.</span>
          </label>
        </div>
      )}

      {f.attending && (
        <Field label="A note for Kenna and Anthony" optional>
          <TextArea value={f.note} onChange={set('note')} placeholder="Share a little well wish, or anything we should know." maxLength={600} />
        </Field>
      )}

      <div style={{ marginTop: 8 }}>
        <label className={`check ${errs.privacyAck ? 'check-err' : ''}`}>
          <input type="checkbox" checked={f.privacyAck} onChange={(e) => set('privacyAck')(e.target.checked)} />
          <span>
            I understand the name, email, and address I provide are collected only to plan the shower
            and send thank-you cards, and are not shared beyond the hosts.
          </span>
        </label>
        {errs.privacyAck && <div className="err-text" style={{ marginLeft: 34 }}>{errs.privacyAck}</div>}
      </div>

      <Turnstile onToken={setTsToken} action="rsvp" />

      {status === 'error' && (
        <Note tone="error" style={{ marginTop: 16 }}>
          We could not save your RSVP just now. Please check your connection and try again.
        </Note>
      )}

      <button type="submit" className="btn btn-primary btn-block" style={{ marginTop: 20 }} disabled={status === 'saving'}>
        {status === 'saving' ? 'Saving your spot...' : status === 'error' ? 'Try again' : 'Save my RSVP'}
      </button>
    </form>
  );
}

/* ------------------------------- Well wishes ----------------------------- */
export function WishForm({ onSaved }) {
  const [f, setF] = useState({ name: '', message: '', email: '' });
  const [errs, setErrs] = useState({});
  const [status, setStatus] = useState('idle');
  const [tsToken, setTsToken] = useState('');
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  async function submit(ev) {
    ev.preventDefault();
    if (status === 'saving') return;
    const e = {};
    if (!f.name.trim()) e.name = 'Please add your name.';
    if (!f.message.trim()) e.message = 'A few words would mean the world.';
    if (f.email && !emailOk(f.email)) e.email = 'That email does not look quite right.';
    setErrs(e);
    if (Object.keys(e).length) return;
    setStatus('saving');
    try {
      await DB.addWish({ name: f.name.trim(), message: f.message.trim(), email: f.email.trim(), turnstileToken: tsToken });
      setStatus('done');
      setF({ name: '', message: '', email: '' });
      window.dispatchEvent(new Event('llb:data-changed'));
      onSaved && onSaved();
    } catch (err) {
      console.error(err);
      setStatus('error');
      setTsToken('');
      if (window.turnstile) window.turnstile.reset();
    }
  }

  if (status === 'done') {
    return (
      <Note tone="sage" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Wildflower size={40} color="#e08a2b" />
        <span>Thank you. Your words are tucked away for Kenna and Anthony to read.{' '}
          <button className="btn btn-sm btn-secondary" style={{ marginLeft: 6 }} onClick={() => setStatus('idle')}>Add another</button>
        </span>
      </Note>
    );
  }

  return (
    <form className="card" onSubmit={submit} noValidate>
      <Field label="Your name" required error={errs.name}>
        <Input value={f.name} onChange={set('name')} invalid={errs.name} />
      </Field>
      <Field label="Your well wishes or advice" required error={errs.message}>
        <TextArea value={f.message} onChange={set('message')} invalid={errs.message} placeholder="A wish for the baby, or advice for the new parents." maxLength={800} />
      </Field>
      <Field label="Email" optional hint="Only if you would like the hosts to be able to reply." error={errs.email}>
        <Input type="email" value={f.email} onChange={set('email')} invalid={errs.email} />
      </Field>
      <Turnstile onToken={setTsToken} action="wish" />
      {status === 'error' && <Note tone="error" style={{ marginTop: 14, marginBottom: 14 }}>We could not save that just now. Please try again.</Note>}
      <div style={{ textAlign: 'center', marginTop: 16 }}>
        <button type="submit" className="btn btn-primary" disabled={status === 'saving'}>
          {status === 'saving' ? 'Sending...' : 'Leave a wish'}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------ Predictions ------------------------------ */
export function PredictionForm({ onSaved }) {
  const [f, setF] = useState({
    name: '', email: '', birthDate: '', weight: '', length: '',
    hairColor: '', eyeColor: '', takesAfter: '', nameGuess: '',
  });
  const [errs, setErrs] = useState({});
  const [status, setStatus] = useState('idle');
  const [tsToken, setTsToken] = useState('');
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const predictionKeys = ['birthDate', 'weight', 'length', 'hairColor', 'eyeColor', 'takesAfter', 'nameGuess'];

  async function submit(ev) {
    ev.preventDefault();
    if (status === 'saving') return;
    const e = {};
    if (!f.name.trim()) e.name = 'Please add your name.';
    if (f.email && !emailOk(f.email)) e.email = 'That email does not look quite right.';
    const anyGuess = predictionKeys.some((k) => String(f[k]).trim() !== '');
    if (!anyGuess) e.guess = 'Make at least one guess below.';
    setErrs(e);
    if (Object.keys(e).length) return;
    setStatus('saving');
    try {
      await DB.addPrediction({
        name: f.name.trim(), email: f.email.trim(),
        birthDate: f.birthDate, weight: f.weight.trim(), length: f.length.trim(),
        hairColor: f.hairColor.trim(), eyeColor: f.eyeColor.trim(),
        takesAfter: f.takesAfter, nameGuess: f.nameGuess.trim(),
        turnstileToken: tsToken,
      });
      setStatus('done');
      window.dispatchEvent(new Event('llb:data-changed'));
      onSaved && onSaved();
    } catch (err) {
      console.error(err);
      setStatus('error');
      setTsToken('');
      if (window.turnstile) window.turnstile.reset();
    }
  }

  if (status === 'done') {
    return (
      <Note tone="sage" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Bee size={42} />
        <span>Your guesses are in. We will see who knows this little one best.</span>
      </Note>
    );
  }

  return (
    <form className="card" onSubmit={submit} noValidate>
      <div className="grid-2">
        <Field label="Your name" required error={errs.name}>
          <Input value={f.name} onChange={set('name')} invalid={errs.name} />
        </Field>
        <Field label="Email" optional hint="So the host can reach the winner." error={errs.email}>
          <Input type="email" value={f.email} onChange={set('email')} invalid={errs.email} />
        </Field>
      </div>
      <hr className="hr" />
      <p className="faint" style={{ fontSize: '0.84rem', marginTop: -6 }}>Guess as many or as few as you like (at least one).</p>
      <div className="grid-2">
        <Field label="Guessed birth date" optional>
          <Input type="date" value={f.birthDate} onChange={set('birthDate')} />
        </Field>
        <Field label="Baby name guess" optional>
          <Input value={f.nameGuess} onChange={set('nameGuess')} placeholder="A hunch?" />
        </Field>
        <Field label="Birth weight" optional>
          <Input value={f.weight} onChange={set('weight')} placeholder="e.g. 7 lb 9 oz" />
        </Field>
        <Field label="Length" optional>
          <Input value={f.length} onChange={set('length')} placeholder="e.g. 20 in" />
        </Field>
        <Field label="Hair color" optional>
          <Input value={f.hairColor} onChange={set('hairColor')} placeholder="e.g. wispy brown" />
        </Field>
        <Field label="Eye color" optional>
          <Input value={f.eyeColor} onChange={set('eyeColor')} placeholder="e.g. blue" />
        </Field>
      </div>
      <Field label="Takes after" optional>
        <div className="choice-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
          {['Mom', 'Dad'].map((opt) => (
            <div key={opt} className={`choice ${f.takesAfter === opt ? 'on' : ''}`} onClick={() => set('takesAfter')(f.takesAfter === opt ? '' : opt)}
                 role="radio" aria-checked={f.takesAfter===opt} tabIndex={0}
                 onKeyDown={(e)=>{ if(e.key==='Enter'||e.key===' '){e.preventDefault();set('takesAfter')(f.takesAfter===opt?'':opt);} }}>
              <span className="dot" /> {opt}
            </div>
          ))}
        </div>
      </Field>
      <Turnstile onToken={setTsToken} action="prediction" />
      {errs.guess && <Note tone="warn" style={{ marginTop: 14, marginBottom: 14 }}>{errs.guess}</Note>}
      {status === 'error' && <Note tone="error" style={{ marginBottom: 14 }}>We could not save that just now. Please try again.</Note>}
      <div style={{ textAlign: 'center', marginTop: 16 }}>
        <button type="submit" className="btn btn-primary" disabled={status === 'saving'}>
          {status === 'saving' ? 'Sending...' : 'Submit my guesses'}
        </button>
      </div>
    </form>
  );
}
